import type { ElementType } from 'react'
import { hasNavProduct } from 'lib/sidebarItems'
import type { NavItemType } from 'types/Sidebar'

export type PageRow = {
  id: string
  label: string
  breadcrumb?: string
  href: string
  icon?: ElementType
  /** Lower-cased strings the palette matches typed text against. */
  keywords: string[]
}

export type PageGates = {
  products: string[] | undefined
  /** Whether the viewer is inside a workspace. Product gates apply only then (TRK-5965). */
  inWorkspace: boolean
  user: { type?: string | null; role?: number | null }
  isFeatureEnabled: (name: string) => boolean
}

/** Words people type that are not the page's label. Keyed by lower-cased label. */
const PAGE_ALIASES: Readonly<Record<string, readonly string[]>> = {
  inbox: [`messages`, `texts`, `sms`],
  pipelines: [`deals`, `opportunities`],
  'action center': [`home`, `dashboard`],
  contacts: [`patients`, `leads`, `people`],
  activities: [`calls`, `forms`],
  tasks: [`todo`, `to-do`],
}

function isVisible(item: NavItemType, gates: PageGates): boolean {
  if (item.if === false) return false
  if (item.divider || item.customComponent || item.isLocked) return false
  if (!hasNavProduct(gates.products, item.product, gates.inWorkspace)) return false
  if (item.superUserOnly && gates.user.type !== `super`) return false
  if (item.feature && !gates.isFeatureEnabled(item.feature)) return false
  // Permission filtering (including child roles) is applied by usePermissionFilteredSidebar.
  return true
}

function toRow(item: NavItemType & { href: string; name: string }, parent?: NavItemType): PageRow {
  const breadcrumb = parent?.name
  // A child answers to its own aliases and its parent's; a top-level item has only its own.
  const aliasKeys = new Set([item.name, ...(parent?.name ? [parent.name] : [])].map((name) => name.toLowerCase()))
  const keywords = new Set(
    [item.name, breadcrumb ?? ``, ...[...aliasKeys].flatMap((key) => PAGE_ALIASES[key] ?? [])]
      .filter(Boolean)
      .map((keyword) => keyword.toLowerCase()),
  )
  return {
    id: `${breadcrumb ?? ``}/${item.name}/${item.href}`,
    label: item.name,
    breadcrumb,
    href: item.href,
    icon: item.icon ?? parent?.icon,
    keywords: [...keywords],
  }
}

const hasLink = (item: NavItemType): item is NavItemType & { href: string; name: string } =>
  typeof item.href === `string` && typeof item.name === `string`

/** Every page the user can reach, in sidebar order. Parents without an href contribute only their children. */
export function flattenNavItems(items: NavItemType[], gates: PageGates): PageRow[] {
  const rows: PageRow[] = []
  for (const item of items) {
    if (!isVisible(item, gates)) continue
    if (hasLink(item)) rows.push(toRow(item))
    for (const child of item.children ?? []) {
      if (!isVisible(child, gates) || !hasLink(child)) continue
      rows.push(toRow(child, item))
    }
  }
  return rows
}

export function matchPageRows(rows: PageRow[], text: string): PageRow[] {
  const needle = text.trim().toLowerCase()
  if (!needle) return rows
  return rows.filter((row) => row.keywords.some((keyword) => keyword.includes(needle)))
}
